'use client';

import { c as _c } from "react/compiler-runtime";
import type { AddFactiiiAction, AddFactiiiState } from '@shared/utils/helpers/addFactiii/formState';
import { taggableError } from '@shared/utils/helpers/addFactiii/sections';
import toast from '@shared/utils/helpers/toast';
import { useQuery } from '@tanstack/react-query';
import { Tag, X } from 'lucide-react';
import type { Dispatch } from 'react';
import { useState } from 'react';
import { client } from '../../../utils/trpc';
import Avatar from '../../common/Avatar';
import FactiiiSearchDropdown from '../../UI/FactiiiSearchDropdown';
import Input from '../../UI/Input';
import Switch from '../../UI/Switch';
import { t, ToggleRow } from './parts';
const Slider = t0 => {
  const $ = _c(24);
  const {
    label,
    value,
    display,
    min,
    max,
    step,
    onChange,
    ends
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className={t.label}>{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== display) {
    t2 = <span className={t.meta}>{display}</span>;
    $[2] = display;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="mb-1.5 flex items-center justify-between">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== onChange) {
    t4 = e => onChange(Number(e.target.value));
    $[7] = onChange;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = `linear-gradient(to right, rgb(var(--primary)) ${(value - min) / (max - min) * 100}%, var(--border-standard) ${(value - min) / (max - min) * 100}%)`;
  let t6;
  if ($[9] !== t5) {
    t6 = {
      background: t5
    };
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== max || $[12] !== min || $[13] !== step || $[14] !== t4 || $[15] !== t6 || $[16] !== value) {
    t7 = <input type="range" min={min} max={max} step={step} value={value} onChange={t4} style={t6} className="h-1.5 w-full cursor-pointer appearance-none rounded-full accent-primary [&::-moz-range-thumb]:size-4 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-primary [&::-webkit-slider-thumb]:size-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary" />;
    $[11] = max;
    $[12] = min;
    $[13] = step;
    $[14] = t4;
    $[15] = t6;
    $[16] = value;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== ends) {
    t8 = ends && <div className={`mt-1 flex justify-between ${t.micro}`}><span>{ends[0]}</span><span>{ends[1]}</span></div>;
    $[18] = ends;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t3 || $[21] !== t7 || $[22] !== t8) {
    t9 = <div>{t3}{t7}{t8}</div>;
    $[20] = t3;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
};
type Props = {
  state: AddFactiiiState;
  dispatch: Dispatch<AddFactiiiAction>;
};
const BotSettings = t0 => {
  const $ = _c(38);
  const {
    state,
    dispatch
  } = t0;
  const [search, setSearch] = useState("");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["available-bot-models"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== state.botEnabled) {
    t2 = {
      queryKey: t1,
      queryFn: _temp,
      enabled: state.botEnabled
    };
    $[1] = state.botEnabled;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    data: modelsData
  } = useQuery(t2);
  let t3;
  let t4;
  if ($[3] !== search || $[4] !== state.spaceSlug) {
    t3 = ["factiiis", "taggable", search, state.spaceSlug];
    t4 = async () => {
      const [error_0, data_0] = await client.search.factiiis.query({
        query: search,
        spaceSlug: state.spaceSlug || undefined
      });
      if (error_0) {
        throw error_0;
      }
      return data_0;
    };
    $[3] = search;
    $[4] = state.spaceSlug;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  let t5;
  if ($[7] !== state.botEnabled || $[8] !== t3 || $[9] !== t4) {
    t5 = {
      queryKey: t3,
      queryFn: t4,
      enabled: state.botEnabled
    };
    $[7] = state.botEnabled;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const {
    data: taggableResult
  } = useQuery(t5);
  let t6;
  if ($[11] !== taggableResult?.factiiis) {
    t6 = taggableResult?.factiiis ?? [];
    $[11] = taggableResult?.factiiis;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== state.taggableFactiiis || $[14] !== t6) {
    let t8;
    if ($[16] !== state.taggableFactiiis) {
      t8 = factiii => !state.taggableFactiiis.some(chosen => chosen.id === factiii.id);
      $[16] = state.taggableFactiiis;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    t7 = t6.filter(t8);
    $[13] = state.taggableFactiiis;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const taggable = t7;
  let t8;
  if ($[18] !== dispatch) {
    t8 = botEnabled => dispatch({
      type: "patch",
      patch: {
        botEnabled
      }
    });
    $[18] = dispatch;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== state.botEnabled || $[21] !== t8) {
    t9 = <ToggleRow title="Reply with AI" subtitle="A bot answers every post tagged with this"><Switch state={state.botEnabled} onChange={t8} /></ToggleRow>;
    $[20] = state.botEnabled;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== dispatch || $[24] !== modelsData?.models || $[25] !== search || $[26] !== state.botEnabled || $[27] !== state.botPrompt || $[28] !== state.canTagMoreThanOne || $[29] !== state.maxOutputTokens || $[30] !== state.modelId || $[31] !== state.taggableFactiiis || $[32] !== state.temperature || $[33] !== taggable) {
    t10 = state.botEnabled && <div className="space-y-3"><div className="space-y-1"><label className={t.label}>AI model</label><select value={state.modelId ?? ""} onChange={e => dispatch({
          type: "patch",
          patch: {
            modelId: Number(e.target.value)
          }
        })} className="w-full rounded-md border border-ds-border-standard bg-transparent px-2.5 py-2 text-sm text-textPrimary transition-colors focus:border-primary focus:outline-none"><option value="">Select a model</option>{modelsData?.models.map(_temp2)}</select></div><Input type="textarea" label="What should the bot do? (optional)" placeholder="Tell the AI how to respond to posts tagged with this factiii" inputClassName="text-sm" rows={3} value={state.botPrompt} onChange={botPrompt => dispatch({
        type: "patch",
        patch: {
          botPrompt
        }
      })} /><Slider label="Reply length" value={state.maxOutputTokens} display={`${state.maxOutputTokens} tokens`} min={50} max={1000} step={10} onChange={maxOutputTokens => dispatch({
        type: "patch",
        patch: {
          maxOutputTokens
        }
      })} /><Slider label="Creativity" value={state.temperature} display={`${state.temperature}%`} min={0} max={100} step={5} onChange={temperature => dispatch({
        type: "patch",
        patch: {
          temperature
        }
      })} ends={["Focused", "Creative"]} /><div className="space-y-2 rounded-lg border border-ds-border-standard p-2.5"><div className="flex items-center gap-2"><Tag className="size-3.5 shrink-0 text-primary" /><div className="min-w-0"><p className={t.label}>Factiiis the bot can tag</p><p className={t.meta}>Optional. The bot picks from these when it replies.</p></div></div><div className="flex items-center justify-between gap-2.5 rounded-lg border border-ds-border-standard px-2.5 py-1.5"><span className="text-xs text-textPrimary">Tag more than one per post</span><Switch state={state.canTagMoreThanOne} onChange={canTagMoreThanOne => dispatch({
            type: "patch",
            patch: {
              canTagMoreThanOne
            }
          })} /></div><FactiiiSearchDropdown searchValue={search} onSearchChange={setSearch} searchResults={taggable} onSelect={factiii_0 => dispatch({
          type: "patch",
          patch: {
            taggableFactiiis: [...state.taggableFactiiis, factiii_0]
          }
        })} placeholder="Search factiiis to add..." label="Search factiiis to add..." validateSelection={_temp3} onValidationError={_temp4} />{state.taggableFactiiis.map(factiii_2 => <div key={factiii_2.id} className="flex items-center gap-2.5 rounded-lg border border-ds-border-standard px-2.5 py-1.5"><Avatar factiiiAvatarKey={factiii_2.avatarKey} factiiiId={factiii_2.id} size={24} /><p className={`min-w-0 flex-1 truncate ${t.label}`}>{factiii_2.name}</p><button type="button" onClick={() => dispatch({
            type: "patch",
            patch: {
              taggableFactiiis: state.taggableFactiiis.filter(chosen_0 => chosen_0.id !== factiii_2.id)
            }
          })} className="shrink-0 rounded-md p-1 text-textSecondary transition-colors hover:bg-ds-border-standard hover:text-destructive" title="Remove"><X size={14} /></button></div>)}</div></div>;
    $[23] = dispatch;
    $[24] = modelsData?.models;
    $[25] = search;
    $[26] = state.botEnabled;
    $[27] = state.botPrompt;
    $[28] = state.canTagMoreThanOne;
    $[29] = state.maxOutputTokens;
    $[30] = state.modelId;
    $[31] = state.taggableFactiiis;
    $[32] = state.temperature;
    $[33] = taggable;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t10 || $[36] !== t9) {
    t11 = <div className="space-y-3">{t9}{t10}</div>;
    $[35] = t10;
    $[36] = t9;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  return t11;
};
export default BotSettings;
async function _temp() {
  const [error, data] = await client.factiiis.availableBotModels.query();
  if (error) {
    throw error;
  }
  return data;
}
function _temp2(model) {
  return <option key={model.id} value={model.id}>{model.description} ({model.capability})</option>;
}
function _temp3(factiii_1) {
  return taggableError(factiii_1.requirements);
}
function _temp4(error_1) {
  return toast(error_1, "", "error");
}